import {useLocation} from "react-router-dom";
import {WHOLESALE_PRICE_SUFFIX} from "../routes";

export const formatNumbersWithCommas = (value) => {
  value = Math.round(value);
  let isPrice = false;
  try {
    const { pathname } = useLocation();
    isPrice = pathname.includes(WHOLESALE_PRICE_SUFFIX);
  }
  catch(e){
    // Non op, used to fix unit tests without memory router
  }

  if (isPrice){
    return "$" + formatNumbersWithCommasRecursive(value);
  }
  return formatNumbersWithCommasRecursive(value);
}

export const formatNumbersWithCommasRecursive = (value) => {
  if (value === null || value === undefined) {
    return "0";
  }
  const transform = (valueArr, returnValue) => {
    if (valueArr.length < 3) {
      return valueArr.concat(returnValue).join("");
    }
    const result = valueArr.slice(valueArr.length - 3);
    const newArray = valueArr.slice(0, valueArr.length - 3);
    if (newArray.length > 0) {
      result.unshift(",");
    }
    return transform(newArray, [...result, ...returnValue]);
  };
  const hasNegative = value < 0;
  const valueToFormat = value.toString().replace("-", "");

  const valueParts = valueToFormat.split(".");
  return `${hasNegative ? "-" : ""}${transform(valueParts[0].split(""), [])}${
    valueParts[1] ? `.${valueParts[1].split("")[0]}` : ""
  }`;
};
export const calculateQuarterlyValues = (
  slicedRow: number[],
  longTermSeasonStartMonth: number,
) => {
  // if 0, then need to put first 5 last (January)
  // if 3, then need to put first 2 last (April)
  // if 6, then just need to put the end first (July)
  // if 9, then need to put end 4 first (October)
  const rearrangedRow =
    longTermSeasonStartMonth < 6
      ? slicedRow
          .slice(5 - longTermSeasonStartMonth)
          .concat(slicedRow.slice(0, 5 - longTermSeasonStartMonth))
      : slicedRow
          .slice(slicedRow.length - 1 - (longTermSeasonStartMonth % 6))
          .concat(
            slicedRow.slice(
              0,
              slicedRow.length - 1 - (longTermSeasonStartMonth % 6),
            ),
          );
  return new Array(4).fill(0).map((_, i) => {
    return rearrangedRow
      .slice(i * 3, (i + 1) * 3)
      .reduce((acc, val) => acc + val, 0);
  });
};
export const getFirstMonthOfLongTerm = (
  incomingData: number[][],
  ccdRowAccessor: number,
  columnAccessor: number,
  lrTotals: number[],
  ccdMonthTotal: number,
  previousMonthTotal: number,
) => {
  const firstLongTermMonthTotal = incomingData
    .slice(0, ccdRowAccessor)
    .reduce((acc, curr) => acc + curr[columnAccessor], 0);
  const thisMonthTotal = lrTotals[0];
  const total =
    ccdMonthTotal +
    thisMonthTotal -
    (previousMonthTotal + firstLongTermMonthTotal);
  return total > 0 ? total : 0;
};

export const calculateCCD = (
  monthsInShortTerm: number,
  incomingData: number[][],
  shipmentMonthTotal: number,
  overrideTotal = 0,
) => {
  // Add 6 to compensate for 6 months before first month
  const row = monthsInShortTerm + 6;
  const column = monthsInShortTerm - 1;
  const monthTotal = incomingData
    .slice(0, row)
    .reduce((acc, curr) => acc + curr[column], 0);
  if (overrideTotal > 0) {
    return {
      shipmentMonth: overrideTotal,
      previousMonthTotal: monthTotal + overrideTotal,
    };
  }
  const shipmentMonth =
    monthTotal < shipmentMonthTotal ? shipmentMonthTotal - monthTotal : 0;
  return { shipmentMonth, previousMonthTotal: monthTotal + shipmentMonth };
};
export const calculateUpcoming = (
  row: number,
  rowVal: number,
  column: number,
  columnVal: number,
  futures_pg2n_totals: number[][],
  lrTotals: number[],
  monthsInFirstTwoSeasons: number,
) => {
  const cupDatum = futures_pg2n_totals[row][0];
  const lrTotal = lrTotals[column - monthsInFirstTwoSeasons];

  const cupDatumLessRowVal = cupDatum - rowVal;
  const lrTotalLessColumnVal = lrTotal - columnVal;
  const result =
    lrTotal > columnVal
      ? cupDatumLessRowVal > lrTotalLessColumnVal
        ? lrTotalLessColumnVal
        : cupDatumLessRowVal
      : 0;
  return result > 0 ? result : 0;
};

// Overrides are for when user updates data in cell. They'll be skipped otherwise
export const autoUpdateForecast = (
  incomingData: number[][],
  monthsInFirstTwoSeasons: number,
  futures_pg2n_totals: number[][],
  lrTotals: number[],
  shipmentMonthTotal: number,
  overrideRow = 0,
  overrideColumn = 0,
  overrideValue = 0,
) => {
  // Rows go back 2 full seasons, so long term season is #4
  // 3 months per season
  const ccdRowAccessor = 6 + monthsInFirstTwoSeasons;
  // start of quarter is offset from start of season by +1. (O indexing erases the +1 difference for first month)
  const columnAccessor = monthsInFirstTwoSeasons;

  // calculate Shipment Month
  const { shipmentMonth, previousMonthTotal } = calculateCCD(
    monthsInFirstTwoSeasons,
    incomingData,
    shipmentMonthTotal,
    overrideColumn === monthsInFirstTwoSeasons - 1 &&
      overrideRow === ccdRowAccessor
      ? overrideValue
      : 0,
  );
  incomingData[ccdRowAccessor][columnAccessor - 1] = shipmentMonth;

  // Calculate CCD Month
  const firstMonthOfLongTerm = getFirstMonthOfLongTerm(
    incomingData,
    ccdRowAccessor,
    columnAccessor,
    lrTotals,
    shipmentMonthTotal,
    previousMonthTotal,
  );
  const ccdMonth =
    overrideColumn === monthsInFirstTwoSeasons && overrideRow === ccdRowAccessor
      ? overrideValue
      : firstMonthOfLongTerm;
  const incomingDataRow = incomingData[ccdRowAccessor];
  incomingDataRow[columnAccessor] = ccdMonth;
  const total = incomingDataRow
    .slice(monthsInFirstTwoSeasons, incomingDataRow.length - 3)
    .reduce((acc, curr) => acc + curr, 0);
  const rowLength = incomingDataRow.length;
  incomingDataRow[rowLength - 3] = total;
  incomingDataRow[rowLength - 1] = total - incomingDataRow[rowLength - 2];
  let precalculatedRows = incomingData
    .slice(ccdRowAccessor)
    .map((row) =>
      row.slice(0, columnAccessor + 1).reduce((acc, curr) => acc + curr, 0),
    );

  let precalculatedColumns = incomingData
    .slice(0, ccdRowAccessor)
    .reduce((acc, row) => {
      const newAcc = [...acc];
      row
        .slice(columnAccessor + 2, row.length - 3)
        .forEach((val, i) => (newAcc[i] = newAcc[i] + val));
      return newAcc;
    }, new Array(12).fill(0));

  // Father, forgive me for modifying in place
  incomingData.forEach((row, i) => {
    // skip precalculated rows
    if (i >= ccdRowAccessor && i <= incomingData.length - 1) {
      row.forEach((column, j) => {
        if (j > columnAccessor && j < incomingData[i].length - 3) {
          const precalculatedRowsIndex = i - ccdRowAccessor;
          const precalculatedColumnIndex = j - columnAccessor - 1;
          // skip precalculated columns
          if (i === overrideRow && j === overrideColumn) {
            const incomingDataDiff = overrideValue - incomingData[i][j];
            incomingData[i][j] = overrideValue;
            incomingData[i][incomingData[i].length - 3] += incomingDataDiff;
            incomingData[i][incomingData[i].length - 1] += incomingDataDiff;
            // use override value because this is currently 0 and not a diff
            precalculatedRows[precalculatedRowsIndex] += overrideValue;
            precalculatedColumns[precalculatedColumnIndex] += overrideValue;
          } else if (
            j > columnAccessor &&
            j < incomingData[i].length - 3 &&
            precalculatedRows[precalculatedRowsIndex] !==
              futures_pg2n_totals[i][0]
          ) {
            const units = Math.round(
              calculateUpcoming(
                i,
                precalculatedRows[precalculatedRowsIndex],
                j,
                precalculatedColumns[precalculatedColumnIndex],
                futures_pg2n_totals,
                lrTotals,
                monthsInFirstTwoSeasons,
              ),
            );
            const incomingDataDiff = units - incomingData[i][j];
            incomingData[i][j] = units;
            // Advance appropriate values in calculations as each subsequent row / column uses the one "above" or "left"
            precalculatedRows[precalculatedRowsIndex] =
              precalculatedRows[precalculatedRowsIndex] + units;
            precalculatedColumns[precalculatedColumnIndex] =
              precalculatedColumns[precalculatedColumnIndex] + units;

            incomingData[i][incomingData[i].length - 3] += incomingDataDiff;
            incomingData[i][incomingData[i].length - 1] += incomingDataDiff;
          } else if (
            precalculatedRows[precalculatedRowsIndex] ===
              futures_pg2n_totals[i][0] &&
            incomingData[i][j] > 0
          ) {
            const diff = 0 - incomingData[i][j];
            incomingData[i][j] = 0;
            incomingData[i][incomingData[i].length - 3] += diff;
            incomingData[i][incomingData[i].length - 1] += diff;
          }
        }
      });
    }
  });

  return incomingData.reduce((acc, curr) => {
    if (acc.length === 0) {
      return curr.slice(0, curr.length - 3).map((val) => val);
    }

    return acc.map((val, i) => val + curr[i]);
  }, [] as number[]);
};
export const autoUpdateForecastInsideCalculations = (
  incomingData: number[][],
  monthsInFirstTwoSeasons: number,
  futures_pg2n_totals: number[][],
  lrTotals: number[],
  updateRow: number,
  updateColumn: number,
  value: number,
) => {
  let precalculatedRows = incomingData
    .slice(updateRow)
    .map((row) =>
      row.slice(0, updateColumn).reduce((acc, curr) => acc + curr, 0),
    );

  let precalculatedColumns = incomingData
    .slice(0, updateRow)
    .reduce((acc, row) => {
      const newAcc = [...acc];
      row
        .slice(updateColumn, row.length - 3)
        .forEach((val, i) => (newAcc[i] = newAcc[i] + val));
      return newAcc;
    }, new Array(12).fill(0));

  incomingData.forEach((row, i) => {
    // skip precalculated rows
    if (i >= updateRow && i <= incomingData.length - 1) {
      row.forEach((column, j) => {
        const precalculatedRowsIndex = i - updateRow;
        const precalculatedColumnIndex = j - updateColumn;
        // skip precalculated columns
        if (i === updateRow && j === updateColumn) {
          const incomingDataDiff = value - incomingData[i][j];
          incomingData[i][j] = value;
          incomingData[i][incomingData[i].length - 3] += incomingDataDiff;
          incomingData[i][incomingData[i].length - 1] += incomingDataDiff;
          // use override value because this is currently 0 and not a diff
          precalculatedRows[precalculatedRowsIndex] += value;
          precalculatedColumns[precalculatedColumnIndex] += value;
        } else if (
          j >= updateColumn &&
          j < incomingData[i].length - 3 &&
          precalculatedRows[precalculatedRowsIndex] !==
            futures_pg2n_totals[i][0]
        ) {
          const units = calculateUpcoming(
            i,
            precalculatedRows[precalculatedRowsIndex],
            j,
            precalculatedColumns[precalculatedColumnIndex],
            futures_pg2n_totals,
            lrTotals,
            monthsInFirstTwoSeasons,
          );
          const incomingDataDiff = units - incomingData[i][j];
          incomingData[i][j] = units;
          // Advance appropriate values in calculations as each subsequent row / column uses the one "above" or "left"
          precalculatedRows[precalculatedRowsIndex] =
            precalculatedRows[precalculatedRowsIndex] + units;
          precalculatedColumns[precalculatedColumnIndex] =
            precalculatedColumns[precalculatedColumnIndex] + units;

          incomingData[i][incomingData[i].length - 3] += incomingDataDiff;
          incomingData[i][incomingData[i].length - 1] += incomingDataDiff;
        }
      });
    }
  });

  return incomingData.reduce((acc, curr) => {
    if (acc.length === 0) {
      return curr.slice(0, curr.length - 3).map((val) => val);
    }

    return acc.map((val, i) => val + curr[i]);
  }, [] as number[]);
};
